import { IS_AWS_ENV } from "constants/app.constants";
import { RIG_LAYER } from "constants/mapLayers.constants";

// Color By options for the rig/frac layer details panel and the map adapter.
// The dropdown label maps to a key from the rig/frac /values endpoint. `type`
// is deliberately not offered.

export type RigFracColorByField =
  | "operator"
  | "county"
  | "basin"
  | "subBasin"
  | "locationType"
  | "stateProvince"
  | "country"
  | "rigTrajectory";

// Applied client-side for a layer that has never had a selection saved.
// Not persisted until the user changes something.
export const RIG_FRAC_COLOR_BY_DEFAULT: RigFracColorByField = "operator";

export interface RigFracColorByOption {
  label: string;
  value: RigFracColorByField;
}

const AZURE_COLOR_BY_OPTIONS: readonly RigFracColorByOption[] = [
  { label: "Current Operator", value: "operator" },
  { label: "County", value: "county" },
  { label: "Basin", value: "basin" },
  { label: "Sub Basin", value: "subBasin" },
  { label: "Location Type", value: "locationType" }
];

// AWS offers the same fields, in the same order, as the rig/frac layer filter
// (EVA-338). Rig Trajectory is Rigs-only there too: a frac crew has no rig.
const AWS_COLOR_BY_OPTIONS: readonly RigFracColorByOption[] = [
  { label: "Operator", value: "operator" },
  { label: "Basin", value: "basin" },
  { label: "Play/Sub Basin", value: "subBasin" },
  { label: "Country", value: "country" },
  { label: "State/Province", value: "stateProvince" },
  { label: "Field/County", value: "county" },
  { label: "Location Type", value: "locationType" },
  { label: "Rig Trajectory", value: "rigTrajectory" }
];

const RIG_ONLY_FIELDS: ReadonlySet<RigFracColorByField> = new Set(["rigTrajectory"]);

// Read per call rather than at module load, so tests can flip IS_AWS_ENV.
export function getRigFracColorByOptions(
  layerName?: Maybe<string>
): readonly RigFracColorByOption[] {
  if (!IS_AWS_ENV) return AZURE_COLOR_BY_OPTIONS;
  return AWS_COLOR_BY_OPTIONS.filter(
    (option) => layerName === RIG_LAYER || !RIG_ONLY_FIELDS.has(option.value)
  );
}

/**
 * The attribute key the layer is coloured by — a rig/frac layer is always
 * coloured by one. A never-set layer, and any saved value we no longer offer
 * (notably the retired "none" sentinel, or Rig Trajectory on Frac Crews),
 * resolve to the Current Operator default; the next save overwrites the stale
 * value.
 */
export function resolveColorByField(
  saved: Maybe<string>,
  layerName?: Maybe<string>
): RigFracColorByField {
  const offered = getRigFracColorByOptions(layerName);
  return saved != null && offered.some((option) => option.value === saved)
    ? (saved as RigFracColorByField)
    : RIG_FRAC_COLOR_BY_DEFAULT;
}

/**
 * The attribute a rig/frac layer is actually coloured by right now, resolving
 * the one precedence rule every surface must agree on: a layer the user has
 * picked by hand reads its own saved field; every other layer follows the
 * attribute the well focus pushed, falling back to its saved field when no
 * focus has pushed one.
 *
 * Shared by the map adapter, the layer filter and the details panel so they
 * cannot disagree about which attribute a layer is showing.
 */
export function resolveEffectiveColorByField(params: {
  saved: Maybe<string>;
  focusColorBy: Maybe<string>;
  isDetached: boolean;
  layerName: Maybe<string>;
}): RigFracColorByField {
  const { saved, focusColorBy, isDetached, layerName } = params;
  return resolveColorByField(isDetached ? saved : focusColorBy ?? saved, layerName);
}
